import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import Section from "../../../common/section";
import Form from "../../../common/form";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { listRequest as roleListRequest } from "../../../../store/slices/rolesSlice";
import {
  departmentRequest,
  designationRequest,
} from "../../../../store/slices/metaSlice";
import {
  createRequest,
  userDetailsRequest,
  updateUserDetailRequest,
  performActionRequest,
} from "../../../../store/slices/usersSlice";
import AdminUserDetailsForm from "./AdminUserDetailsForm";
import { Button } from "primereact/button";
import { AccessScopeConstants } from "../../../../constants";
import MoreActions from "../../../common/moreActions";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";

const AdminUserDetails = ({
  action,
  createRequest,
  departmentRequest,
  departments,
  designationRequest,
  designations,
  error,
  isEdit,
  labels,
  parent,
  record,
  requesting,
  roleListRequest,
  roleQuery,
  roles,
  updateUserDetailRequest,
  user,
  userDetailsRequest,
  performActionRequest,
}) => {
  const intl = useIntl();
  const params = useParams();
  const navigate = useNavigate();
  const [isEditable, setIsEditable] = useState(!isEdit);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  useEffect(() => {
    if (params?.id && isEdit) {
      userDetailsRequest({ id: params.id });
    }
  }, [params?.id, isEdit, userDetailsRequest]);

  useEffect(() => {
    roleListRequest(roleQuery);
  }, [roleListRequest, roleQuery]);

  useEffect(() => {
    departmentRequest();
    designationRequest();
  }, [departmentRequest, designationRequest]);

  const onSubmit = (values) => {
    const payload = {
      ...values,
      department: values.department?.id || values.department,
      designation: values.designation?.id || values.designation,
      role: values.role.id,
      profileType: values.profileType.id,
    };
    if (isEdit) {
      updateUserDetailRequest({ id: record.id, ...payload });
    } else {
      createRequest(payload);
    }
  };

  useEffect(() => {
    if (
      !requesting &&
      !error &&
      (action === "create" || action === "update" || action === "delete")
    ) {
      navigate(parent.to);
    }

    if (
      !requesting &&
      !error &&
      (action === "activate" || action === "deactivate")
    ) {
      userDetailsRequest({ id: params.id });
    }
  }, [
    error,
    action,
    requesting,
    navigate,
    parent.to,
    userDetailsRequest,
    params?.id,
  ]);

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage({ id: "delete_message" }),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        performActionRequest({ action: "delete", id: data.id });
      },
      reject: () => {},
    });
  };

  return (
    <div>
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.email}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            <span
              className="pi pi-arrow-left mr-2 cursor-pointer"
              onClick={(e) => navigate(parent.to)}
            ></span>
            {intl.formatMessage({ id: isEdit ? "user_details" : "add_user" })}
          </div>
          {(isEdit && !isEditable && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-pencil"
                outlined
                onClick={() => setIsEditable(!isEditable)}
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
              <MoreActions
                data={{ ...record }}
                actions={[
                  {
                    label: "delete",
                    icon: "pi-trash",
                    isAllowed: canDelete,
                    action: (data) => onDeleteAction(data),
                  },
                  {
                    label: "activate",
                    icon: "pi-check",
                    isAllowed: canUpdate,
                    action: (data) =>
                      performActionRequest({
                        action: "activate",
                        id: data.id,
                      }),
                    hidden: {
                      field: "isActive",
                      value: true,
                    },
                  },
                  {
                    label: "deactivate",
                    icon: "pi-times",
                    isAllowed: canUpdate,
                    action: (data) =>
                      performActionRequest({
                        action: "deactivate",
                        id: data.id,
                      }),
                    hidden: {
                      field: "isActive",
                      value: false,
                    },
                  },
                ]}
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          <Section title="basic_info">
            <Form
              disabled={!isEditable}
              initialValues={{
                ...record,
                department: record?.department || null,
                designation: record?.designation || null,
                role: record?.role || null,
                profileType: record?.profileType
                  ? {
                      id: 1,
                      name: "Local",
                    }
                  : null,
              }}
              submitBtn={{
                label: intl.formatMessage({
                  id: isEdit ? "update_user" : "add_user",
                }),
                onClick: onSubmit,
                disabled:
                  requesting && (action === "create" || action === "update"),
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) =>
                  isEdit ? setIsEditable(false) : navigate(parent.to),
                disabled:
                  requesting && (action === "create" || action === "update"),
              }}
            >
              <AdminUserDetailsForm
                departments={departments}
                designations={designations}
                roles={roles}
                action={action}
                error={error}
                requesting={requesting}
                disabled={!isEditable}
                isEdit={isEdit}
              />
            </Form>
          </Section>
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  roleQuery: state.roles?.query,
  departments: state?.meta?.departments,
  designations: state?.meta?.designations,
  roles: state?.roles?.data?.list,
  action: state?.users?.action,
  error: state?.users?.error,
  requesting: state?.users?.requesting,
  record: state?.users?.record,
  user: state?.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  roleListRequest: (data) => dispatch(roleListRequest(data)),
  departmentRequest: () => dispatch(departmentRequest()),
  designationRequest: () => dispatch(designationRequest()),
  createRequest: (data) => dispatch(createRequest(data)),
  userDetailsRequest: (data) => dispatch(userDetailsRequest(data)),
  updateUserDetailRequest: (data) => dispatch(updateUserDetailRequest(data)),
  performActionRequest: (data) => dispatch(performActionRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminUserDetails);
